import { Card, Flex, Skeleton, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import ChangeLabel from "./ChangeLabel";
import {
  changeBackground,
  changeColor,
  describeChange,
} from "./deltaFormat";

const { Text } = Typography;

const statusColor = {
  strong: "#389e0d",
  neutral: "#8c8c8c",
  poor: "#cf1322",
};

// The change against the chosen comparison. Hidden when the API hasn't sent one.
const DeltaIndicator = ({ kpi, comparison, isPercentage, invertDelta }) => {
  const compared = kpi.comparisons?.[comparison];
  const change = describeChange({ delta: compared?.delta, isPercentage });
  if (!change) return null;

  return (
    <span
      className="kpi-delta"
      style={{
        color: changeColor(change.direction, invertDelta),
        background: changeBackground(change.direction, invertDelta),
      }}
    >
      <ChangeLabel
        change={change}
        comparison={comparison}
        isPercentage={isPercentage}
        value={kpi.value}
        earlierValue={compared.value}
      />
    </span>
  );
};

const KpiCard = ({
  title,
  kpi,
  comparison,
  isPercentage,
  loading,
  tooltip,
  invertDelta,
}) => {
  if (loading) {
    return (
      <Card className="kpi-card">
        <Skeleton active paragraph={{ rows: 1 }} title={{ width: 80 }} />
      </Card>
    );
  }

  if (!kpi) return null;

  const displayValue = isPercentage
    ? `${(kpi.value * 100).toFixed(0)}%`
    : kpi.value;

  const cardTitle = (
    <Flex align="center" justify="space-between">
      <Text style={{ fontSize: 16 }}>{title}</Text>
      {tooltip && (
        <Tooltip title={tooltip}>
          <InfoCircleOutlined
            style={{ color: "rgba(0,0,0,0.3)", fontSize: 13, cursor: "help" }}
          />
        </Tooltip>
      )}
    </Flex>
  );

  return (
    <Card
      className="kpi-card"
      title={cardTitle}
      styles={{
        header: { minHeight: 36, padding: "0 16px" },
        body: { padding: "12px 16px" },
      }}
    >
      {/* Value row: percentage/number on left, delta on right */}
      <Flex align="baseline" justify="space-between">
        <span
          className="kpi-value"
          style={{ color: statusColor[kpi.status] || "rgba(0,0,0,0.85)" }}
        >
          {displayValue}
        </span>
        <DeltaIndicator
          kpi={kpi}
          comparison={comparison}
          isPercentage={isPercentage}
          invertDelta={invertDelta}
        />
      </Flex>
      {kpi.numerator != null && kpi.denominator != null && (
        <div className="kpi-ratio">
          {kpi.numerator} / {kpi.denominator}
        </div>
      )}
    </Card>
  );
};

export default KpiCard;
